import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { userService } from "@/api/user";
import type { RoleRegisterSession } from "../utils/roleRegisterSession";
import { normalizeEmail } from "@/shared/utils/email";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type RoleRegisterDetailsField = "email" | "role";

type DetailErrors = Partial<Record<RoleRegisterDetailsField, string>>;

interface UseRoleRegisterDetailsFormOptions {
	session: RoleRegisterSession;
	setSession: Dispatch<SetStateAction<RoleRegisterSession>>;
	registrationRole: "creator" | "client" | "agency" | null;
}

/**
 * Owns details-step validation independently from page-level API errors.
 * Editing a field only clears validation belonging to that field.
 */
export const useRoleRegisterDetailsForm = ({
	session,
	setSession,
	registrationRole,
}: UseRoleRegisterDetailsFormOptions) => {
	const [errors, setErrors] = useState<DetailErrors>({});
	const [emailChecking, setEmailChecking] = useState(false);

	const updateDetails = useCallback(
		(partial: Partial<RoleRegisterSession>) => {
			setSession((current) => ({ ...current, ...partial }));
			if ("email" in partial) {
				setErrors((current) => ({ ...current, email: undefined }));
			}
		},
		[setSession],
	);

	const setFieldError = useCallback((field: RoleRegisterDetailsField, message: string) => {
		setErrors((current) => ({ ...current, [field]: message }));
	}, []);

	const clearFieldError = useCallback((field: RoleRegisterDetailsField) => {
		setErrors((current) => ({ ...current, [field]: undefined }));
	}, []);

	const clearErrors = useCallback(() => setErrors({}), []);

	const emailError =
		errors.email || (session.email && !emailPattern.test(session.email) ? "Enter a valid email address." : "");

	const detailsValid = useMemo(
		() =>
			emailPattern.test(session.email) &&
			(registrationRole !== "creator" ||
				(session.firstName.trim().length > 0 && session.lastName.trim().length > 0)) &&
			session.accepted,
		[registrationRole, session.accepted, session.email, session.firstName, session.lastName],
	);
	const canSubmitDetails = detailsValid && !emailChecking && !Object.values(errors).some(Boolean);

	const checkEmailAvailable = useCallback(async (): Promise<boolean> => {
		if (!emailPattern.test(session.email) || session.google) return Boolean(session.google);

		setEmailChecking(true);
		try {
			const result = await userService.checkExists(normalizeEmail(session.email));
			if (result.email) {
				setFieldError("email", "An account already exists for this email. Please log in instead.");
				return false;
			}
			clearFieldError("email");
			return true;
		} catch {
			setFieldError("email", "We couldn't check this email. Please try again.");
			return false;
		} finally {
			setEmailChecking(false);
		}
	}, [clearFieldError, session.email, session.google, setFieldError]);

	return {
		detailsValid,
		canSubmitDetails,
		emailChecking,
		emailError,
		roleError: errors.role || "",
		updateDetails,
		setFieldError,
		clearErrors,
		checkEmailAvailable,
	};
};
